/**
 * Разбиение маршрута на ПОЭТАЖНЫЕ ПЛЕЧИ (31.07.2026).
 *
 * Отраслевой стандарт (MapsIndoors, Mappedin): мультиэтажный маршрут не
 * рисуется одной плоской линией — он разбивается на legs по этажам, целиком
 * показывается плечо ТЕКУЩЕГО этажа, остальные приглушены, а в точке смены
 * этажа стоит значок перехода «на такой-то этаж». Одна плоская линия для
 * маршрута «вход на 1-м → лестница → магазин на 2-м» выглядит бессмысленной
 * петлёй: обе части накладываются друг на друга (замечание владельца 31.07,
 * маршрут до Pelm).
 *
 * Здесь — чистая математика разбиения; отрисовка — во вьюере.
 */
import type { LonLat } from '@app/geo'
import { haversineM } from '@app/geo'
import type { BuiltGraph } from './graph'

export interface RouteLevelLeg {
  readonly level: string
  readonly polyline: readonly LonLat[]
}

export interface RouteLevelTransition {
  /** Где стоит значок перехода (точка узла на этаже, С КОТОРОГО уходим). */
  readonly at: LonLat
  readonly fromLevel: string
  readonly toLevel: string
  /** stairs | escalator | elevator | … — вид перехода для значка. */
  readonly kind: string
}

export interface RouteLevelSplit {
  readonly legs: readonly RouteLevelLeg[]
  readonly transitions: readonly RouteLevelTransition[]
}

/**
 * Разбивает последовательность узлов маршрута на этажные плечи.
 *
 * Синтетические узлы графа (пристыкованные `~c`) уровень несут, поэтому lookup
 * идёт по графу, а не по venue.network. Узел без уровня (не должен случаться)
 * наследует уровень предыдущего — маршрут не рвётся из-за одной дырки в данных.
 */
interface SplitState {
  legs: { level: string; pts: LonLat[] }[]
  transitions: RouteLevelTransition[]
  prevLevel: string | null
  prevKind: string
  prevPoint: LonLat | null
}

export function splitByLevel(graph: BuiltGraph, nodeIds: readonly string[]): RouteLevelSplit {
  const st: SplitState = {
    legs: [],
    transitions: [],
    prevLevel: null,
    prevKind: '',
    prevPoint: null,
  }
  for (const id of nodeIds) {
    const node = graph.nodes.get(id)
    if (!node) continue
    const level: string | null = node.level ?? st.prevLevel
    if (level === null) continue
    takeNode(st, level, node.point, node.kind)
  }
  return {
    // Плечо из одной точки рисовать нечем — но и терять его нельзя молча:
    // такое бывает только у вырожденных данных, фильтруем осознанно.
    legs: st.legs
      .filter((l) => l.pts.length >= 2 || st.legs.length === 1)
      .map((l) => ({ level: l.level, polyline: dedupe(l.pts) })),
    transitions: st.transitions,
  }
}

/** Один узел маршрута: продолжить плечо или открыть новое с записью перехода. */
function takeNode(st: SplitState, level: string, point: LonLat, kind: string): void {
  const last = st.legs[st.legs.length - 1]
  if (last && last.level === level) {
    last.pts.push(point)
  } else {
    if (last && st.prevPoint) {
      st.transitions.push({
        at: st.prevPoint,
        fromLevel: last.level,
        toLevel: level,
        // Вид перехода: узел-переход обычно и есть лестница/эскалатор/лифт;
        // берём его тип, а не тип соседа-коридора.
        kind: kind !== 'junction' ? kind : st.prevKind || kind,
      })
    }
    st.legs.push({ level, pts: st.prevPoint && last ? [st.prevPoint, point] : [point] })
  }
  st.prevLevel = level
  st.prevKind = kind
  st.prevPoint = point
}

/** Слипшиеся точки (вертикальные пары стоят в одном месте) — одна точка. */
function dedupe(points: readonly LonLat[]): LonLat[] {
  const out: LonLat[] = []
  for (const p of points) {
    const prev = out[out.length - 1]
    if (prev && haversineM(prev, p) < 0.5) continue
    out.push(p)
  }
  return out
}
